import { CalendarDate, parseDate, type DateValue } from "@internationalized/date";
import { DatePicker } from "@its-engineering-and-development/ui-foundation/components";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { useEffect, useRef, useState } from "react";
import { createDateRangeFromToday, getDateRangeButtonLabel, normalizeDashboardDateRange } from "./dashboard-date-range";
import type { DashboardDateRange, DashboardDateRangePreset } from "./dashboard-types";

const DATE_PICKER_FIELD_CLASS =
    "w-full [&_button]:!h-10 [&_button]:!w-full [&_button]:!justify-between [&_button]:!rounded-lg [&_button]:!border [&_button]:!border-gray-300 [&_button]:!px-3 [&_button]:!text-sm [&_button]:!font-normal [&_button]:!text-gray-500 [&_button]:!shadow-none";

const QUICK_OPTIONS: Array<{ label: DashboardDateRangePreset; days: number }> = [
    { label: "7 Days", days: 7 },
    { label: "30 Days", days: 30 },
    { label: "90 Days", days: 90 },
    { label: "1 Year", days: 365 },
];

function toCalendarDate(value: string | null): CalendarDate | null {
    if (!value) return null;

    try {
        return parseDate(value);
    } catch {
        return null;
    }
}

function toDateKey(date: DateValue): string {
    const calendarDate = new CalendarDate(date.year, date.month, date.day);
    const month = String(calendarDate.month).padStart(2, "0");
    const day = String(calendarDate.day).padStart(2, "0");
    return `${calendarDate.year}-${month}-${day}`;
}

function IconCalendar() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M3 10h18M8 2v4M16 2v4" strokeLinecap="round" />
        </svg>
    );
}

export interface DateRangeFilterProps {
    value: DashboardDateRange;
    onChange: (range: DashboardDateRange) => void;
    className?: string;
}

/** Date range picker used by the dashboard global and chart filters. */
export function DateRangeFilter({ value, onChange, className }: DateRangeFilterProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [startDate, setStartDate] = useState<string | null>(value.startDate);
    const [endDate, setEndDate] = useState<string | null>(value.endDate);

    useEffect(() => {
        setStartDate(value.startDate);
        setEndDate(value.endDate);
    }, [value.endDate, value.startDate]);

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (containerRef.current && !containerRef.current.contains(target)) {
                setOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [open]);

    const applyQuickSelect = (days: number, preset: DashboardDateRangePreset) => {
        const nextRange = createDateRangeFromToday(days, preset);

        setStartDate(nextRange.startDate);
        setEndDate(nextRange.endDate);
        onChange(nextRange);
        setOpen(false);
    };

    const applyCustomRange = () => {
        if (!startDate || !endDate) return;

        onChange(
            normalizeDashboardDateRange({
                preset: "Custom",
                startDate,
                endDate,
                days: 0,
            }),
        );
        setOpen(false);
    };

    const isCustomRangeValid = Boolean(startDate && endDate);

    return (
        <div ref={containerRef} className={className}>
            <div className="relative">
                <button
                    type="button"
                    aria-expanded={open}
                    aria-haspopup="dialog"
                    className={cx(
                        "flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5 text-sm transition-colors",
                        open ? "border-brand text-fg-brand-primary" : "border-gray-200 text-gray-700 hover:bg-gray-50",
                    )}
                    onClick={() => setOpen((current) => !current)}
                >
                    <IconCalendar />
                    {getDateRangeButtonLabel(value)}
                </button>

                {open ? (
                    <div
                        role="dialog"
                        aria-label="Date range filter"
                        className="absolute right-0 z-[120] mt-2 w-[min(100vw-2rem,26rem)] rounded-xl border border-gray-200 bg-white p-4 shadow-lg"
                    >
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <p className="mb-3 text-sm font-semibold text-gray-900">Custom Date Range</p>
                                <div className="flex flex-col gap-3">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-500">Start Date</span>
                                        <DatePicker
                                            aria-label="Start Date"
                                            value={toCalendarDate(startDate)}
                                            onChange={(nextDate) => setStartDate(nextDate ? toDateKey(nextDate) : null)}
                                            className={DATE_PICKER_FIELD_CLASS}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-gray-500">End Date</span>
                                        <DatePicker
                                            aria-label="End Date"
                                            value={toCalendarDate(endDate)}
                                            onChange={(nextDate) => setEndDate(nextDate ? toDateKey(nextDate) : null)}
                                            className={DATE_PICKER_FIELD_CLASS}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        disabled={!isCustomRangeValid}
                                        className={cx(
                                            "mt-1 w-full rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                                            isCustomRangeValid
                                                ? "bg-brand-solid text-white hover:bg-brand-solid_hover"
                                                : "cursor-not-allowed bg-gray-100 text-gray-400",
                                        )}
                                        onClick={applyCustomRange}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>

                            <div>
                                <p className="mb-3 text-sm font-semibold text-gray-900">Quick Select</p>
                                <div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-1">
                                    {QUICK_OPTIONS.map((option) => {
                                        const isSelected = value.preset === option.label;

                                        return (
                                            <button
                                                key={option.label}
                                                type="button"
                                                className={cx(
                                                    "rounded-md px-3 py-2 text-left text-sm transition-colors",
                                                    isSelected ? "bg-white font-semibold text-fg-brand-primary shadow-sm" : "text-gray-700 hover:bg-white/70",
                                                )}
                                                onClick={() => applyQuickSelect(option.days, option.label)}
                                            >
                                                {option.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
